import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  Switch,
  FormControlLabel,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Autocomplete,
  Avatar
} from '@mui/material';
import { UploadSimple } from '@phosphor-icons/react';
import api from '../../utils/api';
import { getAvatarSrc, getStaffMemberDisplayPhoto } from '../../utils/avatarConstants';
import { useBusiness } from '../../context/BusinessContext';

function AddStaffModal({ open, onClose, onStaffAdded, businessId }) {
  const { selectedBusiness } = useBusiness();
  const [formData, setFormData] = useState({
    name: '',
    role: '',
    profileImage: '',
    showOnProfile: false,
    notes: '',
    selectedBusinessMember: null
  });
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [businessMembers, setBusinessMembers] = useState([]);
  const [loadingMembers, setLoadingMembers] = useState(false);

  const effectiveBusinessId = businessId || selectedBusiness?.business?._id;

  // Fetch BusinessMembers when modal opens
  useEffect(() => {
    if (open && effectiveBusinessId) {
      fetchBusinessMembers();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- verified intentional deps
  }, [open, effectiveBusinessId]);

  // Reset form when modal closes
  useEffect(() => {
    if (!open) {
      setFormData({
        name: '',
        role: '',
        profileImage: '',
        showOnProfile: false,
        notes: '',
        selectedBusinessMember: null
      });
      setError('');
    }
  }, [open]);

  const fetchBusinessMembers = async () => {
    if (!effectiveBusinessId) return;
    
    setLoadingMembers(true);
    try {
      const response = await api.get(`/api/business-members/business/${effectiveBusinessId}`);
      const members = response.data.members || [];
      
      // Filter out members that already have StaffMember records
      const staffResponse = await api.get(`/api/supplier/staff-members?businessId=${effectiveBusinessId}`);
      const existingStaffMemberIds = new Set(
        (staffResponse.data.staffMembers || [])
          .filter(sm => sm.businessMember)
          .map(sm => (sm.businessMember._id || sm.businessMember)?.toString())
      );
      
      // Only show members that don't already have StaffMember records
      const availableMembers = members.filter(m => !existingStaffMemberIds.has(m._id?.toString()));
      
      setBusinessMembers(availableMembers);
    } catch (err) {
      // Don't show error - just continue without dropdown
      setBusinessMembers([]);
    } finally {
      setLoadingMembers(false);
    }
  };

  const handleBusinessMemberSelect = (member) => {
    if (member) {
      const userName = `${member.user?.firstName || ''} ${member.user?.lastName || ''}`.trim();
      setFormData(prev => ({
        ...prev,
        selectedBusinessMember: member,
        name: userName || member.invitedEmail || prev.name,
        role: member.role || prev.role, // Auto-populate role from BusinessMember
        profileImage: member.user ? '' : prev.profileImage
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        selectedBusinessMember: null,
        role: '', // Clear role when deselecting
        profileImage: ''
      }));
    }
  };

  const handleChange = (field) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setError('Please select an image file');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setError('Image size must be less than 5MB');
      return;
    }

    setUploading(true);
    setError('');

    try {
      const formDataToUpload = new FormData();
      formDataToUpload.append('image', file);

      const response = await api.post('/api/supplier/upload-image', formDataToUpload, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      setFormData(prev => ({ ...prev, profileImage: response.data.imageUrl }));
    } catch (err) {
      setError('Failed to upload image. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      setError('Name is required');
      return;
    }

    setSaving(true);
    setError('');

    try {
      const response = await api.post('/api/supplier/staff-members', {
        name: formData.name.trim(),
        role: formData.selectedBusinessMember ? undefined : (formData.role.trim() || undefined),
        profileImage: formData.selectedBusinessMember?.user ? undefined : (formData.profileImage || undefined),
        showOnProfile: formData.showOnProfile,
        notes: formData.notes.trim() || undefined,
        linkToBusinessMemberId: formData.selectedBusinessMember?._id || undefined,
        ...(effectiveBusinessId ? { businessId: String(effectiveBusinessId) } : {})
      }, {
        params: effectiveBusinessId ? { businessId: String(effectiveBusinessId) } : undefined
      });

      onStaffAdded(response.data.staffMember);
      
      // Reset form
      setFormData({
        name: '',
        role: '',
        profileImage: '',
        showOnProfile: false,
        notes: '',
        selectedBusinessMember: null
      });
      setError('');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to add staff member. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const hasLinkedPlatformUser = !!formData.selectedBusinessMember?.user;
  const linkedAddPreview = formData.selectedBusinessMember
    ? getStaffMemberDisplayPhoto({
        user: formData.selectedBusinessMember.user,
        profileImage: formData.profileImage
      })
    : null;

  const handleClose = () => {
    if (!saving) {
      setFormData({
        name: '',
        role: '',
        profileImage: '',
        showOnProfile: false,
        notes: '',
        selectedBusinessMember: null
      });
      setError('');
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add team member</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Team members can be scheduled. They don't need an account.
        </Typography>
        <Stack spacing={3} sx={{ mt: 1 }}>
          {error && (
            <Alert severity="error" onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          <Autocomplete
            options={businessMembers}
            getOptionLabel={(option) => {
              if (!option.user) return option.invitedEmail || 'Unknown';
              return `${option.user.firstName || ''} ${option.user.lastName || ''}`.trim() || option.user.email || 'Unknown';
            }}
            value={formData.selectedBusinessMember}
            onChange={(_, newValue) => handleBusinessMemberSelect(newValue)}
            loading={loadingMembers}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Link to existing user (Optional)"
                placeholder="Select a user from Users & Access"
                helperText="Select a user who already has platform access to link them"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingMembers ? <CircularProgress size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  )
                }}
              />
            )}
            renderOption={(props, option) => {
              const userName = option.user 
                ? `${option.user.firstName || ''} ${option.user.lastName || ''}`.trim() || option.user.email
                : option.invitedEmail || 'Unknown';
              return (
                <Box component="li" {...props}>
                  <Stack>
                    <Typography variant="body1">{userName}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {option.user?.email || option.invitedEmail} • Role: {option.role}
                    </Typography>
                  </Stack>
                </Box>
              );
            }}
            disabled={saving}
            isOptionEqualToValue={(option, value) => option._id === value._id}
          />

          <TextField
            label="Name"
            required
            fullWidth
            value={formData.name}
            onChange={handleChange('name')}
            placeholder="e.g., John Smith"
            disabled={saving || !!formData.selectedBusinessMember}
            helperText={formData.selectedBusinessMember ? 'Name is set from linked account' : undefined}
          />

          {!formData.selectedBusinessMember && (
            <TextField
              label="Role"
              fullWidth
              value={formData.role}
              onChange={handleChange('role')}
              placeholder="e.g., Photographer, Bartender"
              disabled={saving}
              helperText="Optional - helps identify what this person does"
            />
          )}

          <Box>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Profile image {hasLinkedPlatformUser ? '' : '(optional)'}
            </Typography>
            {hasLinkedPlatformUser ? (
              <Stack spacing={1} alignItems="flex-start">
                <Avatar
                  src={getAvatarSrc(linkedAddPreview)}
                  alt=""
                  sx={{ width: 80, height: 80, fontSize: '1.25rem' }}
                >
                  {(formData.name || '?').charAt(0)}
                </Avatar>
                <Typography variant="caption" color="text.secondary" sx={{ maxWidth: 320 }}>
                  Photo comes from their account (Account Settings). They can change it there.
                </Typography>
              </Stack>
            ) : (
              <>
                <input
                  accept="image/*"
                  style={{ display: 'none' }}
                  id="profile-image-upload"
                  type="file"
                  onChange={handleImageUpload}
                  disabled={uploading || saving}
                />
                <label htmlFor="profile-image-upload">
                  <Button
                    variant="outlined"
                    component="span"
                    startIcon={<UploadSimple size={16} />}
                    disabled={uploading || saving}
                    fullWidth
                  >
                    {uploading ? 'Uploading...' : formData.profileImage ? 'Change image' : 'Upload image'}
                  </Button>
                </label>
                {formData.profileImage && (
                  <Box sx={{ mt: 1 }}>
                    <img
                      src={formData.profileImage}
                      alt="Profile preview"
                      style={{ maxWidth: '100%', maxHeight: 200, borderRadius: 8 }}
                    />
                  </Box>
                )}
              </>
            )}
          </Box>

          <FormControlLabel
            control={
              <Switch
                checked={formData.showOnProfile}
                onChange={handleChange('showOnProfile')}
                disabled={saving}
              />
            }
            label="Show on public profile"
          />

          <TextField
            label="Notes"
            fullWidth
            multiline
            rows={3}
            value={formData.notes}
            onChange={handleChange('notes')}
            placeholder="Internal notes about this staff member..."
            disabled={saving}
            helperText="Optional - only visible to you"
          />

          <Typography variant="body2" color="text.secondary" sx={{ mt: -1 }}>
            {formData.selectedBusinessMember
              ? 'This staff member will be linked to their existing account and can be scheduled immediately.'
              : 'This person can be scheduled immediately. You can give them platform access later if needed.'}
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={saving || uploading || !formData.name.trim()}
          startIcon={saving ? <CircularProgress size={16} /> : null}
        >
          {saving ? 'Adding...' : 'Add Staff Member'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default AddStaffModal;

